import { apiClient } from './client';

export interface UserCreditsResponse {
  user_id: string;
  credits: number;
}

export interface UseCreditResponse {
  success: boolean;
  remaining_credits: number;
  reason?: string;
}

export interface CheckoutResponse {
  session_id: string;
  checkout_url: string;
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  price_id: string;
  unit_amount: number;
  quantity: number;
  total_amount: number;
  currency: string;
  formatted_price: string;
  payment_type: 'bonus' | 'single';
}

export interface StripeProductsResponse {
  products: StripeProduct[];
  bonus?: StripeProduct | null;
  single?: StripeProduct | null;
}

export async function getUserCredits(userId: string): Promise<number> {
  if (!userId) return 0;
  try {
    const res = await apiClient.get<UserCreditsResponse>(`/payments/credits`, {
      params: { user_id: userId },
    });
    return res.data?.credits ?? 0;
  } catch (error) {
    console.error('Error fetching user credits:', error);
    return 0;
  }
}

export async function consumeUserCredit(userId: string): Promise<UseCreditResponse> {
  const res = await apiClient.post<UseCreditResponse>('/payments/use-credit', {
    user_id: userId,
  });
  return res.data;
}

export async function getStripeProducts(): Promise<StripeProductsResponse> {
  try {
    const res = await apiClient.get<StripeProductsResponse>('/payments/products');
    return res.data;
  } catch (error) {
    console.error('Error fetching Stripe products:', error);
    return { products: [] };
  }
}

export async function createCheckoutSession(
  userId: string,
  quantity: number = 1,
  paymentType: 'bonus' | 'single' = 'bonus',
  priceId?: string,
  successUrl?: string,
  cancelUrl?: string,
): Promise<CheckoutResponse> {
  const res = await apiClient.post<CheckoutResponse>('/payments/checkout', {
    user_id: userId,
    quantity,
    payment_type: paymentType,
    price_id: priceId,
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
  return res.data;
}

export async function verifyPaymentSession(sessionId: string): Promise<{ status: string }> {
  const res = await apiClient.get<{ status: string }>('/payments/verify-session', {
    params: { session_id: sessionId },
  });
  return res.data;
}
